/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Misha Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Julia Ignatieva
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.0.0
 */

import { useCallback } from 'react';

import isFunction from 'lodash/isFunction';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { AccessComponentKeyEnum, FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import UserNotifications from '@/main/modules/userNotifications/components/UserNotifications';
import { makeSelectUserNotificationsSettings } from '@/main/modules/userNotifications/selectors';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectAuthSearchSettingsSearchBoxVisible,
    makeSelectFeatureEnabled,
    makeSelectUserIsChatAvailable,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { DChatButton } from '@modules/dchat/DChatButton';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { makeSelectGuardComponents } from '@modules/role';
import { guardComponentType } from '@modules/role/reducer/guardReducer';
import { makeSelectSuperSearchIsOpen } from '@modules/superSearch/superSearchSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { ICorporateTranslation } from '../../modules/internationalization/types';
import ICommonTranslation from '../../modules/internationalization/types/common';
import Guard from '../../modules/role/Guard';
import SuperSearch from '../../modules/superSearch/components/SuperSearch';
import CorporateHeaderMenuItem from './components/CorporateHeaderMenuItem';
import HeaderSelector from './components/HeaderSelector';
import {
    CorporateHeaderContainerStyled,
    HeaderNotificationsContainerStyled,
    HeaderSelectorContainerStyled,
    TabMenuContainerStyled,
    TabMenuCounterStyled,
} from './components/styled/CorporateHeaderStyled';
import { tabMenuItems } from './corporateHeaderConstants';
import { ICorporateHeaderConfigItem, ICorporateHeaderProps } from './corporateHeaderTypes';

const CorporateHeader = (props: ICorporateHeaderProps & IConnectedProps & IDispatchProps) => {
    const {
        guardComponentTypes,
        offerCounter,
        superSearchIsOpen,

        translation,
        isSearchBoxVisible,
        logBusinessEvent,
    } = props;

    const { offers, ...restItems } = tabMenuItems(translation.tabMenuCompactView);

    const location = useLocation();

    const notificationsSettings = useSelector(makeSelectUserNotificationsSettings());
    const isCampaignsImprovementsFeature = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CAMPAIGNS_IMPROVEMENT));
    const isCvmNotificationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.CVM_NOTIFICATIONS));
    const isDChatEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.DCHAT));
    const isChatAvailable = !!useSelector(makeSelectUserIsChatAvailable());
    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());

    const isDChatButtonShown = isDChatEnabled && isChatAvailable;
    const showOffers = isCampaignsImprovementsFeature ? offerCounter > 0 : true;

    const showNotifications = notificationsSettings?.showNotifications || isCvmNotificationsEnabled;

    const getItemUrl = useCallback(
        (url: ICorporateHeaderConfigItem['url']) => (isFunction(url) ? url() : url),
        [],
    );

    const isItemExternalUrl = useCallback(
        (isExternal: ICorporateHeaderConfigItem['urlIsExternal']) =>
            isFunction(isExternal) ? isExternal(guardComponentTypes) : isExternal,
        [guardComponentTypes],
    );

    return (
        <CorporateHeaderContainerStyled>
            <TabMenuContainerStyled className={superSearchIsOpen ? 'super-search-opened' : ''}>
                {
                    // Пункты меню
                    map(sortBy(restItems, 'order'), (item: ICorporateHeaderConfigItem) => (
                        <Guard accessKey={item.accessKey} key={item.accessKey}>
                            <CorporateHeaderMenuItem
                                {...item}
                                logBusinessEvent={logBusinessEvent}
                                selected={item.isUrlSelected(location.pathname)}
                                url={getItemUrl(item.url)}
                                urlIsExternal={isItemExternalUrl(item.urlIsExternal)}
                            />
                        </Guard>
                    ))
                }
                {/* Пункт меню: "Спецпредложения" */}
                {showOffers &&
                    <Guard accessKey={offers.accessKey}>
                        <CorporateHeaderMenuItem
                            {...offers}
                            logBusinessEvent={logBusinessEvent}
                            selected={offers.isUrlSelected(location.pathname)}
                            url={getItemUrl(offers.url)}
                            urlIsExternal={isItemExternalUrl(offers.urlIsExternal)}
                        >
                            <TabMenuCounterStyled>{offerCounter}</TabMenuCounterStyled>
                        </CorporateHeaderMenuItem>
                    </Guard>
                }

                {showNotifications && (
                    <Guard accessKey={AccessComponentKeyEnum.notifications}>
                        <HeaderNotificationsContainerStyled $withMargin={!isSearchBoxVisible}>
                            <UserNotifications />
                        </HeaderNotificationsContainerStyled>
                    </Guard>
                )}

                {isDChatButtonShown && (
                    <ArrowTooltip
                        disableHoverListener={!isServiceUser}
                        message={translation.serviceUser.tooltip}
                    >
                        <span>
                            <DChatButton isDisabled={isServiceUser}/>
                        </span>
                    </ArrowTooltip>
                )}

                {/* Супер поиск */}
                {isSearchBoxVisible && <SuperSearch />}
            </TabMenuContainerStyled>
            {/* Выпадающее меню */}
            <HeaderSelectorContainerStyled>
                <HeaderSelector translation={translation} />
            </HeaderSelectorContainerStyled>
        </CorporateHeaderContainerStyled>
    );
};

export interface IConnectedProps {
    isSearchBoxVisible?: boolean;
    guardComponentTypes: guardComponentType;
    superSearchIsOpen?: boolean;
    translation: ICommonTranslation & ICorporateTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isSearchBoxVisible: makeSelectAuthSearchSettingsSearchBoxVisible(),
    guardComponentTypes: makeSelectGuardComponents(),
    superSearchIsOpen: makeSelectSuperSearchIsOpen(),
    translation: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CorporateHeader));
